import { useState, useEffect } from "react";
import { Gauge, Save, RefreshCw } from "lucide-react";
import { supabase } from "../integrations/supabase/client";
import { toast } from "sonner";

interface PlanLimit {
  id: string;
  plan: string;
  max_messages_per_month: number;
  max_contacts: number;
  max_triggers: number;
  ai_responses_per_month: number;
  max_users: number;
}

export function Limits() {
  const [limits, setLimits] = useState<PlanLimit[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLimits();
  }, []);

  const fetchLimits = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("plan_limits")
        .select("*")
        .order("plan");
      setLimits(data || []);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateLimit = (plan: string, field: keyof PlanLimit, value: number) => {
    setLimits((prev) =>
      prev.map((l) => (l.plan === plan ? { ...l, [field]: value } : l)),
    );
  };

  const saveLimits = async () => {
    setSaving(true);
    try {
      for (const limit of limits) {
        await supabase
          .from("plan_limits")
          .update({
            max_messages_per_month: limit.max_messages_per_month,
            max_contacts: limit.max_contacts,
            max_triggers: limit.max_triggers,
            ai_responses_per_month: limit.ai_responses_per_month,
            max_users: limit.max_users,
          })
          .eq("id", limit.id);
      }
      toast.success("Limites atualizados com sucesso!");
    } catch (error) {
      toast.error("Erro ao salvar limites");
    } finally {
      setSaving(false);
    }
  };

  const planLabels: Record<string, string> = {
    starter: "Starter (R$ 149)",
    pro: "Pro (R$ 349)",
    business: "Business (R$ 799)",
    enterprise: "Enterprise",
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Limites por Plano
          </h1>
          <p className="text-slate-400">
            Configure os limites de recursos para cada plano
          </p>
        </div>
        <button
          onClick={saveLimits}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 admin-gradient rounded-lg text-white font-medium disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Salvar Alterações
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {limits.map((limit) => (
            <div
              key={limit.id}
              className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-red-500/20">
                  <Gauge className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">
                  {planLabels[limit.plan] || limit.plan}
                </h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Mensagens/mês
                  </label>
                  <input
                    type="number"
                    value={limit.max_messages_per_month}
                    onChange={(e) =>
                      updateLimit(
                        limit.plan,
                        "max_messages_per_month",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Contatos Máx.
                  </label>
                  <input
                    type="number"
                    value={limit.max_contacts}
                    onChange={(e) =>
                      updateLimit(
                        limit.plan,
                        "max_contacts",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Triggers Máx.
                  </label>
                  <input
                    type="number"
                    value={limit.max_triggers}
                    onChange={(e) =>
                      updateLimit(
                        limit.plan,
                        "max_triggers",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Respostas IA/mês
                  </label>
                  <input
                    type="number"
                    value={limit.ai_responses_per_month}
                    onChange={(e) =>
                      updateLimit(
                        limit.plan,
                        "ai_responses_per_month",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Usuários Máx.
                  </label>
                  <input
                    type="number"
                    value={limit.max_users}
                    onChange={(e) =>
                      updateLimit(
                        limit.plan,
                        "max_users",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
